// Add charge sheet — slides up like SettleSheet.
// Posts to /v2/provider/billing/charges. Genuinely a different verb from
// settle (it grows the ledger), kept as a separate sheet rather than a
// method on the SettleSheet selector.
//
// Opened without an encounter — the patient panel's Add Charges — it asks
// which encounter the charge is for before it can submit (gdn-4177). It
// used to create every such charge at the account level, where patient
// credit cannot reach it, and billers typed the visit date into the
// description instead. Same question and rules as the Collect payment
// composer (gdn-3086): nothing preselected, account-level a deliberate peer.

import { useEffect, useId, useState } from 'react';
import { useCaptureCharges } from '@features/billing/store/hooks';
import { ELIG_TOKENS } from '@features/eligibility-checks/theme/readinessTokens';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  InputAdornment,
  MenuItem,
  TextField,
  Typography,
} from '@mui/material';
import { useEventTracking } from '@services/statsig';
import { useSnackbar } from 'notistack';

import { useRecentEncounterOptions } from '../hooks/useRecentEncounterOptions';
import { chargeAttributionSourceOf, chargeEncounterIdOf } from '../utils/chargeAttribution';

import {
  type NewChargeAttribution,
  SettleChargeAttributionSection,
} from './SettleChargeAttribution';
import type { SheetSurfaceVariant } from './SheetSurface';
import { SheetSurface } from './SheetSurface';

const CHARGE_TYPE_OPTIONS = [
  { value: 'no_show', label: 'No-show fee', code: '' },
  { value: 'late_cancel', label: 'Late-cancel fee', code: '' },
  { value: 'paper_form', label: 'Paper-form fee', code: '' },
  { value: 'cpt_99214', label: 'CPT 99214 (OON visit)', code: 'CPT:99214' },
  { value: 'other', label: 'Other', code: '' },
] as const;

type ChargeKind = (typeof CHARGE_TYPE_OPTIONS)[number]['value'];

interface Props {
  patientId: string;
  encounterId?: string | null;
  /** Chrome to render inside — see SheetSurface (gdn-2957). */
  surface?: SheetSurfaceVariant;
  /** Analytics source tag: the surface that opened the sheet. */
  source?: string;
  onClose: () => void;
}

export const AddChargeSheet = ({
  patientId,
  encounterId,
  surface = 'drawer',
  source,
  onClose,
}: Props) => {
  const { enqueueSnackbar } = useSnackbar();
  const { payment } = useEventTracking();
  const { captureCharges, isLoading } = useCaptureCharges();
  const [amountStr, setAmountStr] = useState('');
  const [kind, setKind] = useState<ChargeKind>('no_show');
  const [description, setDescription] = useState('');
  const [submitError, setSubmitError] = useState<string | null>(null);

  // Null until the biller answers, and never defaulted: a recency guess is
  // the same inference gdn-3023 refuses, by another name.
  const needsAttribution = !encounterId;
  const [attribution, setAttribution] = useState<NewChargeAttribution | null>(null);
  const {
    visits,
    isLoading: isVisitsLoading,
    hasOlder,
    showOlder,
    isLoadingOlder,
  } = useRecentEncounterOptions({ patientId, skip: !needsAttribution });

  // A picked encounter that drops out of the refreshed list must not ride
  // the payload while the picker can no longer show it — clear it and ask
  // again, as the Collect payment composer does (gdn-3086).
  useEffect(() => {
    if (
      attribution?.kind === 'encounter' &&
      !isVisitsLoading &&
      !visits.some(visit => visit.id === attribution.encounterId)
    ) {
      setAttribution(null);
    }
  }, [attribution, isVisitsLoading, visits]);

  const cents = Math.round((Number(amountStr) || 0) * 100);
  const attributionMissing = needsAttribution && attribution === null;
  const canSubmit = !isLoading && cents > 0 && !attributionMissing;
  // Names the open question beside the disabled button (gdn-4176's rule).
  // An empty amount explains itself.
  const blockedReason =
    cents > 0 && attributionMissing
      ? 'Answer “Which encounter is this charge for?” above to continue.'
      : null;
  const blockedReasonId = useId();

  const handleAttributionChange = (next: NewChargeAttribution) => {
    // At selection, not submit: a choice that is abandoned, or whose
    // charge fails, still counts against billing_charges_captured's
    // new_charge_attribution (same rule as the Collect payment composer).
    payment.addChargeAttributionSelected({
      kind: next.kind,
      source: source ?? 'ledger_drawer',
    });
    setAttribution(next);
  };

  const handleShowOlder = () => {
    payment.chargeAttributionShowOlderClicked({
      surface: 'add_charge',
      listed_count: visits.length,
    });
    showOlder();
  };

  const handleSubmit = async () => {
    setSubmitError(null);
    const option = CHARGE_TYPE_OPTIONS.find((o) => o.value === kind);
    const chargeEncounterId = chargeEncounterIdOf(encounterId, attribution);
    const result = await captureCharges({
      patient_id: patientId,
      encounter_id: chargeEncounterId,
      items: [
        {
          charge_amount_cents: cents,
          billing_code: option?.code || undefined,
          description: description || option?.label || 'Patient charge',
          units: 1,
        },
      ],
    });
    if (!result.success) {
      const err = result.error;
      setSubmitError(err instanceof Error ? err.message : 'Failed to add charge');
      return;
    }
    payment.chargesCaptured({
      encounter_id: chargeEncounterId ?? undefined,
      item_count: 1,
      total_charge_cents: cents,
      new_charge_attribution: chargeAttributionSourceOf(encounterId, attribution),
    });
    enqueueSnackbar(`Added ${dollars(cents)} charge`, { variant: 'success' });
    onClose();
  };

  return (
    <SheetSurface variant={surface} drawerMaxHeight="80%">
        <Box
          sx={{
            px: 3,
            py: 1.5,
            borderBottom: `1px solid ${ELIG_TOKENS.line}`,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Typography variant="subtitle1" sx={{ fontWeight: 600, color: ELIG_TOKENS.midnight }}>
            Add charge
          </Typography>
          <Button
            onClick={onClose}
            size="small"
            sx={{ color: ELIG_TOKENS.inkSoft, textTransform: 'none' }}
          >
            Cancel
          </Button>
        </Box>

        <Box sx={{ flex: 1, overflowY: 'auto', px: 3, py: 2 }}>
          <Box sx={{ mb: 1.5 }}>
            <TextField
              label="Amount"
              value={amountStr}
              onChange={(e) => setAmountStr(e.target.value)}
              fullWidth
              size="small"
              autoFocus
              slotProps={{
                input: {
                  startAdornment: <InputAdornment position="start">$</InputAdornment>,
                },
              }}
            />
          </Box>
          <Box sx={{ mb: 1.5 }}>
            <TextField
              select
              label="Type"
              value={kind}
              onChange={(e) => setKind(e.target.value as ChargeKind)}
              fullWidth
              size="small"
            >
              {CHARGE_TYPE_OPTIONS.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </TextField>
          </Box>
          {/* Before the description on purpose: it is the field billers
              were using to say which visit a charge belonged to. */}
          {needsAttribution && (
            <SettleChargeAttributionSection
              visits={visits}
              value={attribution}
              onChange={handleAttributionChange}
              isVisitsLoading={isVisitsLoading}
              onShowOlder={handleShowOlder}
              hasOlder={hasOlder}
              isLoadingOlder={isLoadingOlder}
            />
          )}
          <Box sx={{ mb: 1.5 }}>
            <TextField
              label="Description (optional)"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              fullWidth
              size="small"
              multiline
              minRows={2}
            />
          </Box>

          {submitError && <Alert severity="error">{submitError}</Alert>}
        </Box>

        <Box
          sx={{
            borderTop: `1px solid ${ELIG_TOKENS.line}`,
            bgcolor: ELIG_TOKENS.bg,
            px: 3,
            py: 1.5,
            display: 'flex',
            justifyContent: 'flex-end',
            alignItems: 'center',
            gap: 1,
          }}
        >
          {blockedReason && (
            <Typography
              id={blockedReasonId}
              variant="caption"
              sx={{ color: ELIG_TOKENS.amber, fontWeight: 600, flex: 1, minWidth: 0, mr: 'auto' }}
            >
              {blockedReason}
            </Typography>
          )}
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={!canSubmit}
            aria-describedby={blockedReason ? blockedReasonId : undefined}
            sx={{
              bgcolor: ELIG_TOKENS.midnight,
              color: '#fff',
              '&:hover': { bgcolor: ELIG_TOKENS.midnight, filter: 'brightness(0.9)' },
              minWidth: 140,
            }}
          >
            {isLoading ? <CircularProgress size={18} sx={{ color: '#fff' }} /> : 'Add charge'}
          </Button>
        </Box>
    </SheetSurface>
  );
};

const dollars = (cents: number): string => {
  const abs = Math.abs(cents);
  if (abs % 100 === 0) return `$${abs / 100}`;
  return `$${(abs / 100).toFixed(2)}`;
};
